import { apiClient } from '../utils/api';
import { UrlBuilder } from '../utils/urls';

export interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  is_read: boolean;
  created_at: string;
  data?: any;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: number;
}

class NotificationsService {
  // Get all notifications for a user with token
  async getNotifications(token: string, userId: number): Promise<ApiResponse<Notification[]>> {
    try {
      const response = await apiClient.getWithToken<Notification[]>(
        UrlBuilder.notifications.all(userId),
        token
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Mark a notification as read with token
  async markAsRead(token: string, notificationId: number): Promise<ApiResponse> {
    try {
      const response = await apiClient.postWithToken(
        UrlBuilder.notifications.markAsRead(notificationId),
        token,
        {}
      );

      if (response.success) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return {
          success: false,
          error: response.error,
          code: response.code
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }

  // Mark all notifications as read with token
  async markAllAsRead(token: string, userId: number): Promise<ApiResponse> {
    try {
      const notifications = await this.getNotifications(token, userId);
      
      if (!notifications.success || !notifications.data) {
        return notifications;
      }

      // Mark each unread notification as read
      const unreadNotifications = notifications.data.filter(n => !n.is_read);
      const promises = unreadNotifications.map(notification => 
        this.markAsRead(token, notification.id)
      );

      await Promise.all(promises);
      
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      };
    }
  }
}

export const notificationsService = new NotificationsService();